import React from 'react';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { FormControlLabel, Stack, styled, Typography } from '@mui/material';
import { TextField } from 'components/TextField/TextField';
import { DateField } from 'components/DateField/DateField';
import { Select } from 'components/Select/Select';
import { PhoneInput } from 'components/PhoneInput/PhoneInput';
import { ProfileType, SignupCaregiverRelationship } from 'features/signUp/types';
import { Checkbox } from 'components';
import { StateSelector } from 'components/StateSelector/StateSelector';
import { useProfileCreationStep } from './useProfileCreationStep';

interface ProfileCreationStepProps {
  profileType: ProfileType;
  caregiverMemberRelationships?: SignupCaregiverRelationship[];
}

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  alignItems: 'flex-start',
  color: theme.palette.grey[800],
  '& .MuiCheckbox-root': {
    padding: '0 9px 0 0',
  },
  '& .Typography-root': {
    color: theme.palette.grey[800],
  },
}));

export const ProfileCreationStep = ({
  profileType,
  caregiverMemberRelationships,
}: ProfileCreationStepProps) => {
  const { translation } = useTranslationContext();
  const {
    titleTranslationKey,
    subtitleTranslationKey,
    showGenderDropdown,
    showRelationshipToMemberDropdown,
    showPhoneNumberField,
    stateOfResidenceDropdownData,
    territoriesDropdownData,
    genderDropdownData,
    isDOBFieldDisabled,
    isNameFieldDisabled,
    inputController,
    isNotificationFlagEnabled,
    showEmailField,
    smsOptInLabel,
    showSmsOptInCheckbox,
  } = useProfileCreationStep(
    profileType,
    translation('empty_field_error'),
    translation('phone_number_invalid_error'),
  );
  return (
    <Stack className="profile-creation-step" justifyContent="flex-start">
      <Stack className="profile-creation-step__header" spacing={3}>
        <Typography className="header__title" variant="h5" color="primary">
          {translation(titleTranslationKey)}
        </Typography>
        {subtitleTranslationKey && (
          <Typography className="header__subtitle" variant="body1" color="grey.800">
            {translation(subtitleTranslationKey)}
          </Typography>
        )}
      </Stack>
      <Stack className="profile-creation-step__form" spacing={3} paddingTop={4}>
        <TextField
          id="signup-profile-creation-step__first-name-text-field"
          label={translation('first_name')}
          disabled={isNameFieldDisabled}
          error={
            inputController.firstName.fieldState.isTouched &&
            !!inputController.firstName.fieldState.error
          }
          helperText={
            inputController.firstName.fieldState.isTouched
              ? inputController.firstName.fieldState.error?.message
              : undefined
          }
          {...inputController.firstName.field}
        />
        <TextField
          id="signup-profile-creation-step__last-name-text-field"
          label={translation('last_name')}
          disabled={isNameFieldDisabled}
          error={
            inputController.lastName.fieldState.isTouched &&
            !!inputController.lastName.fieldState.error
          }
          helperText={
            inputController.lastName.fieldState.isTouched
              ? inputController.lastName.fieldState.error?.message
              : undefined
          }
          {...inputController.lastName.field}
        />
        <DateField
          className="signup-profile-creation-step__date-of-birth-field"
          label={translation('date_of_birth')}
          error={
            inputController.dateOfBirth.fieldState.isTouched &&
            !!inputController.dateOfBirth.fieldState.error
          }
          helperText={
            inputController.dateOfBirth.fieldState.isTouched
              ? inputController.dateOfBirth.fieldState.error?.message
              : undefined
          }
          disabled={isDOBFieldDisabled}
          {...inputController.dateOfBirth.field}
        />
        {showRelationshipToMemberDropdown && (
          <Select
            id="signup-profile-creation-step__relationship-to-member-select"
            label={translation('caregiver_info_relationship_title')}
            options={
              caregiverMemberRelationships?.map((relationship) => ({
                label: relationship.translation,
                value: relationship.relationship,
              })) || []
            }
            optionLabelKey="label"
            optionValueKey="value"
            error={
              inputController.caregiverRelationshipToMember.fieldState.isTouched &&
              !!inputController.caregiverRelationshipToMember.fieldState.error
            }
            helperText={
              inputController.caregiverRelationshipToMember.fieldState.isTouched
                ? inputController.caregiverRelationshipToMember.fieldState.error?.message
                : undefined
            }
            {...inputController.caregiverRelationshipToMember.field}
            value={
              typeof inputController.caregiverRelationshipToMember.field.value === 'string'
                ? inputController.caregiverRelationshipToMember.field.value
                : ''
            }
            renderValue={(selected) =>
              caregiverMemberRelationships?.find((r) => r.relationship === selected)?.translation ||
              (selected as string)
            }
          />
        )}
        {showGenderDropdown && (
          <Select
            id="signup-profile-creation-step__gender-select"
            label={translation('gender')}
            options={genderDropdownData.map((option) => ({
              ...option,
              label: translation(`${option.label}`),
            }))}
            optionLabelKey="label"
            optionValueKey="value"
            error={
              inputController.gender.fieldState.isTouched &&
              !!inputController.gender.fieldState.error
            }
            helperText={
              inputController.gender.fieldState.isTouched
                ? inputController.gender.fieldState.error?.message
                : undefined
            }
            {...inputController.gender.field}
            value={
              typeof inputController.gender.field.value === 'string'
                ? inputController.gender.field.value
                : ''
            }
          />
        )}
        <StateSelector
          id="signup-profile-creation-step__state-of-residence-select"
          label={translation('state_of_residence')}
          states={stateOfResidenceDropdownData}
          territories={territoriesDropdownData}
          error={!!inputController.stateOfResidence.fieldState.error}
          helperText={inputController.stateOfResidence.fieldState.error?.message}
          {...inputController.stateOfResidence.field}
          value={
            typeof inputController.stateOfResidence.field.value === 'string'
              ? inputController.stateOfResidence.field.value
              : ''
          }
          translation={translation}
          renderValue={(selected) => {
            // Look for the selected value in both data sets
            const selectedState = stateOfResidenceDropdownData.find(
              (option) => option.value === selected,
            );
            if (selectedState) return selectedState.label;

            const selectedTerritory = territoriesDropdownData.find(
              (option) => option.value === selected,
            );
            if (selectedTerritory) return selectedTerritory.label;
            return '';
          }}
          onChange={(e) => {
            inputController.stateOfResidence.field.onChange(e);
          }}
        />
        {showPhoneNumberField && (
          <PhoneInput
            id="signup-profile-creation-step__phone-input"
            label={translation('cell_phone_number')}
            error={
              inputController.phoneNumber.fieldState.isTouched &&
              !!inputController.phoneNumber.fieldState.error
            }
            helperText={
              inputController.phoneNumber.fieldState.isTouched
                ? inputController.phoneNumber.fieldState.error?.message
                : undefined
            }
            {...inputController.phoneNumber.field}
          />
        )}
        {showEmailField && (
          <TextField
            id="signup-profile-creation-step__email-text-field"
            label={translation('email_address')}
            error={
              inputController.memberProfileEmail.fieldState.isTouched &&
              !!inputController.memberProfileEmail.fieldState.error
            }
            helperText={
              inputController.memberProfileEmail.fieldState.isTouched
                ? inputController.memberProfileEmail.fieldState.error?.message
                : undefined
            }
            {...inputController.memberProfileEmail.field}
          />
        )}
        {showPhoneNumberField && showSmsOptInCheckbox && (
          <Checkbox
            labelId="signup-profile-creation-step__sms-opt-in-checkbox"
            checkboxId="signup-profile-creation-step__sms-opt-in-checkbox-input"
            checked={Boolean(inputController.smsOptIn.field.value)}
            label={smsOptInLabel}
            {...inputController.smsOptIn.field}
          />
        )}
      </Stack>
    </Stack>
  );
};
